
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Newspaper, Calendar, Users, TrendingUp, Plus, Send, Edit3, Eye } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface NewsSubmission {
  id: string;
  title: string;
  content: string;
  category: string;
  status: 'draft' | 'submitted' | 'approved' | 'published' | 'rejected';
  submittedAt: string;
  views: number;
  publishedAt?: string;
}

const StartupNewsroomFeature: React.FC = () => {
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState<NewsSubmission[]>([]);
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'startup-news'
  });

  const categories = [
    { value: 'startup-news', label: 'Startup News' },
    { value: 'product-launch', label: 'Product Launch' },
    { value: 'funding', label: 'Funding Announcement' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'achievement', label: 'Achievement' },
    { value: 'team-update', label: 'Team Update' }
  ];

  const submitStory = () => {
    if (!formData.title || !formData.content) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    const newSubmission: NewsSubmission = {
      id: Date.now().toString(),
      title: formData.title,
      content: formData.content,
      category: formData.category,
      status: 'submitted',
      submittedAt: new Date().toISOString(),
      views: 0
    };

    setSubmissions([newSubmission, ...submissions]);
    setFormData({ title: '', content: '', category: 'startup-news' });
    setShowSubmissionForm(false);

    toast({
      title: "Story Submitted!",
      description: "Your story has been submitted for review and will be featured in the TeenVisor newsroom."
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'submitted': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'published': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryLabel = (value: string) => {
    const category = categories.find(c => c.value === value);
    return category ? category.label : value;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Newspaper className="h-5 w-5 text-blue-600" />
            Startup Feature on TeenVisor Newsroom
          </CardTitle>
          <CardDescription>
            Get your startup featured in the TeenVisor newsroom and reach thousands of engaged teen entrepreneurs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Newsroom Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 text-center">
                <Newspaper className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold text-blue-700">{submissions.filter(s => s.status === 'published').length}</div>
                <p className="text-sm text-blue-600">Published Stories</p>
              </CardContent>
            </Card>
            
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <TrendingUp className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <div className="text-2xl font-bold text-green-700">
                  {submissions.reduce((sum, s) => sum + s.views, 0)}
                </div>
                <p className="text-sm text-green-600">Total Views</p>
              </CardContent>
            </Card>
            
            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                <div className="text-2xl font-bold text-purple-700">10K+</div>
                <p className="text-sm text-purple-600">Monthly Readers</p>
              </CardContent>
            </Card>
            
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4 text-center">
                <Calendar className="h-6 w-6 mx-auto mb-2 text-orange-600" />
                <div className="text-2xl font-bold text-orange-700">{submissions.length}</div>
                <p className="text-sm text-orange-600">Total Submissions</p>
              </CardContent>
            </Card>
          </div>

          {/* Submit New Story */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Your Newsroom Submissions</h3>
              <Button 
                onClick={() => setShowSubmissionForm(!showSubmissionForm)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Submit New Story
              </Button>
            </div>

            {showSubmissionForm && (
              <Card className="mb-6 border-blue-200">
                <CardContent className="p-6">
                  <h4 className="font-medium mb-4">Submit Your Startup Story</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Story Title *</label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        placeholder="e.g., Our AI Startup Just Raised $1M to Help Teens Learn Coding"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full p-2 border rounded-md"
                      >
                        {categories.map(category => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Story Content *</label>
                      <Textarea
                        value={formData.content}
                        onChange={(e) => setFormData({...formData, content: e.target.value})}
                        placeholder="Share your startup's story, achievements, milestones, or news. What makes your story compelling for teen entrepreneurs?"
                        rows={6}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={submitStory} className="bg-blue-600 hover:bg-blue-700">
                      <Send className="h-4 w-4 mr-2" />
                      Submit for Review
                    </Button>
                    <Button variant="outline" onClick={() => setShowSubmissionForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Submissions List */}
          <div className="space-y-4">
            {submissions.length === 0 ? (
              <div className="text-center py-12">
                <Newspaper className="h-16 w-16 mx-auto mb-4 text-blue-600" />
                <h3 className="text-xl font-semibold mb-2">No Stories Submitted Yet</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Share your startup's story with thousands of teen entrepreneurs. Get featured in our exclusive newsroom.
                </p>
                <Button 
                  onClick={() => setShowSubmissionForm(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Submit Your First Story
                </Button>
              </div>
            ) : (
              submissions.map((submission) => (
                <Card key={submission.id} className="border-l-4 border-l-blue-600">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold">{submission.title}</h3>
                        <div className="flex gap-2 mt-1">
                          <Badge className={getStatusColor(submission.status)}>
                            {submission.status}
                          </Badge>
                          <Badge variant="outline">
                            {getCategoryLabel(submission.category)}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">
                          Submitted: {new Date(submission.submittedAt).toLocaleDateString()}
                        </div>
                        {submission.status === 'published' && (
                          <div className="text-sm font-medium text-green-600">
                            {submission.views} views
                          </div>
                        )}
                      </div>
                    </div>

                    <p className="text-muted-foreground mb-4 line-clamp-3">
                      {submission.content}
                    </p>

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      {submission.status === 'published' && (
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-2" />
                          View Published
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Newsroom Guidelines */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-6">
              <h4 className="font-medium mb-3">Newsroom Submission Guidelines</h4>
              <ul className="text-sm space-y-2 text-blue-800">
                <li>• Stories should be relevant to teen entrepreneurs and the startup ecosystem</li>
                <li>• Content must be original and provide value to our community</li>
                <li>• Include specific details, metrics, or insights when possible</li>
                <li>• Stories are reviewed within 48-72 hours</li>
                <li>• Approved stories reach 10,000+ monthly teen readers</li>
              </ul>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
};

export default StartupNewsroomFeature;
